/**
 * New audit — three steps, and nothing is created until the last one.
 *
 *     1  Client and period
 *     2  Choose data        the data scope: files and conditions, live count
 *     3  Choose analyses    then "Create audit" or "Create audit and run analyses"
 *
 * Creating locks the scope's rows into the audit (at least one file must be
 * chosen — no file means no rows), records the chosen analyses, and on "run"
 * starts them on the server.
 *
 * The module is the one in the URL — chosen on the client's module page
 * and hub before the audits list this is opened from — and is not changed here.
 */

import { useModule } from '../modules/useModule';
import { auditPath, auditsCrumbs } from '../modules/flow';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
	Link,
	useNavigate,
	useParams,
	useSearchParams,
} from 'react-router-dom';
import {
	Box,
	Button,
	CircularProgress,
	Stack,
	Step,
	StepLabel,
	Stepper,
} from '@mui/material';
import { Card, ErrorNotice, Note } from '../../shared/ui';
import { DatePicker, Field, Select } from '../../shared/fields';
import { useCreateAuditMutation } from '../../queries/audits';
import { useClientsQuery } from '../../queries/clients';
import {
	useAnalysisAvailabilityQuery,
	useAnalysisCatalogueQuery,
} from '../../queries/analysis';
import { count } from '../../utils/format';
import {
	financialYearLabel,
	financialYearOf,
	periodFor,
	type PeriodSpan,
} from '../../utils/period';
import { ErrorState, LoadingState } from '../shared/states';
import { PageHeader } from '../../provider/pageTitle';
import { DataScopeEditor, useScopeCount } from './DataScopeEditor';
import { AnalysisPicker } from './AnalysisPicker';
import type { CanonicalRowFilter } from '../../schemas';

const STEPS = ['Client and period', 'Choose data', 'Choose analyses'];

const PERIOD_TYPES: Array<{ value: PeriodSpan; label: string }> = [
	{ value: 'FY', label: 'Financial year' },
	{ value: 'H1', label: 'Half year' },
	{ value: 'Q1', label: 'Quarter' },
	{ value: 'CUSTOM', label: 'Custom dates' },
];

export default function CreateAudit() {
	const navigate = useNavigate();
	const [search] = useSearchParams();
	/* Under a client (`/clients/3/modules/o2c/audits/new`) the path names it. */
	const { clientId: pathClientId } = useParams();
	const [step, setStep] = useState(0);

	const { module, link } = useModule();
	/* Every client of the firm (each is in every module). */
	const clients = useClientsQuery(module);
	const catalogue = useAnalysisCatalogueQuery(module);

	const thisYear = useMemo(() => financialYearOf(), []);
	const opening = useMemo(() => periodFor(thisYear, 'FY'), [thisYear]);
	const [fy, setFy] = useState(thisYear);
	const [span, setSpan] = useState<PeriodSpan>('FY');
	const [form, setForm] = useState({
		clientId: pathClientId ?? search.get('client') ?? '',
		name: '',
		periodStart: opening.start,
		periodEnd: opening.end,
	});
	const [nameTouched, setNameTouched] = useState(false);
	const [scope, setScope] = useState<CanonicalRowFilter>({});
	const [chosen, setChosen] = useState<Set<string>>(new Set());

	/* A different module is a different set of clients and data: start over
     rather than carry a client, scope or analyses across. */
	const startedIn = useRef(module);
	useEffect(() => {
		if (startedIn.current === module) return;
		startedIn.current = module;
		setStep(0);
		setForm((current) => ({ ...current, clientId: '' }));
		setScope({});
		setChosen(new Set());
	}, [module]);

	const client = (clients.data ?? []).find(
		(item) => item.pkClientId === form.clientId,
	);
	/* Opened from one client's audits: the trail runs back through that client. */
	const fromClientId = pathClientId ?? search.get('client');
	const fromClient = (clients.data ?? []).find(
		(item) => item.pkClientId === fromClientId,
	);
	const periodValid =
		form.periodStart !== '' && form.periodEnd >= form.periodStart;
	/* The suggested name follows the client and year until somebody types one. */
	const suggestedName = client
		? `${client.name} — ${financialYearLabel(fy)}`
		: '';
	const name = nameTouched ? form.name : suggestedName;

	const applyPeriod = (nextFy: number, nextSpan: PeriodSpan) => {
		setFy(nextFy);
		setSpan(nextSpan);
		if (nextSpan === 'CUSTOM') return;
		const next = periodFor(nextFy, nextSpan);
		setForm((current) => ({
			...current,
			periodStart: next.start,
			periodEnd: next.end,
		}));
	};

	const scopeCount = useScopeCount(
		form.clientId || null,
		scope,
		Boolean(form.clientId),
	);
	const analyses = catalogue.data ?? [];
	const create = useCreateAuditMutation();

	/* Ready/Blocked for the whole catalogue, for this client and data scope —
     the backend is the one source of truth; nothing here is re-derived
     (docs/analyses-reference.md spec §8). Kept live once files are chosen in
     step 2, so it is ready the moment step 3 is reached; with no files there is
     no scope to judge. */
	const availability = useAnalysisAvailabilityQuery(
		form.clientId || null,
		module,
		scope,
		Boolean(form.clientId) && (scope.fileIds?.length ?? 0) > 0,
	);
	const availabilityBySlug = useMemo(
		() =>
			new Map(
				availability.data?.analyses.map((item) => [item.slug, item]) ??
					[],
			),
		[availability.data],
	);
	const isBlockedSlug = (slug: string) =>
		availabilityBySlug.get(slug)?.availability === 'BLOCKED';

	/* If the scope changes after choices were made and one of them is now
     blocked, drop it rather than silently submit a choice the backend would
     refuse to run. */
	useEffect(() => {
		if (!availability.data) return;
		setChosen((current) => {
			const next = new Set(current);
			let changed = false;
			for (const slug of current) {
				if (isBlockedSlug(slug)) {
					next.delete(slug);
					changed = true;
				}
			}
			return changed ? next : current;
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [availability.data]);

	const canLeave = [
		/* `client`, not the id: a `?client=` from another module is not one. */
		Boolean(client) && name.trim().length > 0 && periodValid,
		scopeCount.ready,
		chosen.size > 0,
	];
	const blockedReason = [
		'Choose a client, name the audit and check the period.',
		'Choose at least one file, and make sure at least one row is in scope.',
		'Choose at least one analysis.',
	];

	const submit = (run: boolean) =>
		create.mutate(
			{
				clientId: form.clientId,
				name: name.trim(),
				periodStart: form.periodStart,
				periodEnd: form.periodEnd,
				module,
				filter: scope,
				analysisSlugs: Array.from(chosen),
				run,
			},
			{
				onSuccess: (created) =>
					navigate(`${auditPath(created)}/analyses`, {
						replace: true,
					}),
			},
		);

	return (
		<div className="stack wizard-page">
			<PageHeader
				breadcrumbs={
					fromClientId
						? [
								...auditsCrumbs(
									{
										id: fromClientId,
										name: fromClient?.name,
									},
									module,
								),
								{
									label: 'New audit',
									to: link(
										`/audits/new?client=${fromClientId}`,
									),
								},
							]
						: [{ label: 'Audits', to: '/audits' }]
				}
				title="New audit"
			/>

			<Card>
				{/* Shows where you are; moving between steps is Back / Continue only. */}
				<Stepper activeStep={step}>
					{STEPS.map((label, index) => (
						<Step key={label} completed={index < step}>
							<StepLabel>{label}</StepLabel>
						</Step>
					))}
				</Stepper>
			</Card>

			{step === 0 && (
				<Card title="Client and period">
					<ErrorState
						error={clients.error}
						onRetry={clients.refetch}
					/>
					{clients.isPending ? (
						<LoadingState label="Loading clients…" />
					) : (
						<Stack gap="var(--s4)" sx={{ minWidth: 0 }}>
							<div className="grid g2">
								<Field label="Client" required>
									{(id) => (
										<Select
											id={id}
											value={form.clientId}
											placeholder="Choose a client"
											searchable
											options={(clients.data ?? []).map(
												(item) => ({
													value: item.pkClientId,
													label: item.name,
													hint: item.code,
												}),
											)}
											onChange={(next) => {
												setForm((current) => ({
													...current,
													clientId: next,
												}));
												/* A scope built for another client would be meaningless. */
												setScope({});
											}}
										/>
									)}
								</Field>
								<Field label="Audit name" required>
									{(id) => (
										<input
											id={id}
											className="ctl"
											value={name}
											placeholder="e.g. Sales and returns audit FY 2025-26"
											onChange={(event) => {
												setNameTouched(true);
												setForm((current) => ({
													...current,
													name: event.target.value,
												}));
											}}
										/>
									)}
								</Field>
							</div>
							<div className="grid g4">
								<Field label="Financial year" required>
									{(id) => (
										<Select
											id={id}
											value={String(fy)}
											options={[
												thisYear + 1,
												thisYear,
												thisYear - 1,
												thisYear - 2,
											].map((year) => ({
												value: String(year),
												label: financialYearLabel(year),
											}))}
											onChange={(next) =>
												applyPeriod(Number(next), span)
											}
										/>
									)}
								</Field>
								<Field label="Period type">
									{(id) => (
										<Select
											id={id}
											value={span}
											options={PERIOD_TYPES}
											onChange={(next) =>
												applyPeriod(
													fy,
													next as PeriodSpan,
												)
											}
										/>
									)}
								</Field>
								<Field label="Start date" required>
									{(id) => (
										<DatePicker
											id={id}
											value={form.periodStart}
											onChange={(next) => {
												setSpan('CUSTOM');
												setForm((current) => ({
													...current,
													periodStart: next,
												}));
											}}
										/>
									)}
								</Field>
								<Field
									label="End date"
									required
									error={
										!periodValid && form.periodEnd
											? 'The period ends before it starts.'
											: undefined
									}
								>
									{(id) => (
										<DatePicker
											id={id}
											value={form.periodEnd}
											onChange={(next) => {
												setSpan('CUSTOM');
												setForm((current) => ({
													...current,
													periodEnd: next,
												}));
											}}
										/>
									)}
								</Field>
							</div>
						</Stack>
					)}
				</Card>
			)}

			{step === 1 && (
				<Card
					title="Choose data"
					hint="Which of this client's processed rows the audit covers."
				>
					{!form.clientId || !client ? (
						<Note tone="info" row>
							Choose a client in step 1 first.
						</Note>
					) : (
						<DataScopeEditor
							clientId={form.clientId}
							clientName={client.name}
							value={scope}
							onChange={setScope}
						/>
					)}
				</Card>
			)}

			{step === 2 && (
				<Stack gap="var(--s4)" sx={{ minWidth: 0 }}>
					<Card
						title="Choose analyses"
						actions={
							<span className="mut">
								{count(chosen.size)} of {count(analyses.length)}{' '}
								chosen
							</span>
						}
					>
						<ErrorState
							error={catalogue.error}
							onRetry={catalogue.refetch}
						/>
						<ErrorState
							error={availability.error}
							onRetry={availability.refetch}
						/>
						<div className="card-bd flush">
							<AnalysisPicker
								analyses={analyses}
								chosen={chosen}
								onChange={setChosen}
								availability={availabilityBySlug}
								availabilityLoading={availability.isPending}
								loading={catalogue.isPending}
							/>
						</div>
					</Card>
					<Card title="Review">
						<dl className="kv">
							<dt>Client</dt>
							<dd>
								{client?.name} ({client?.code})
							</dd>
							<dt>Audit</dt>
							<dd>{name}</dd>
							<dt>Period</dt>
							<dd>
								{form.periodStart} to {form.periodEnd}
							</dd>
							<dt>Rows in scope</dt>
							<dd>
								{scopeCount.rowCount === null ? (
									<CircularProgress
										size={12}
										aria-label="Loading"
									/>
								) : (
									count(scopeCount.rowCount)
								)}
							</dd>
							<dt>Analyses</dt>
							<dd>{count(chosen.size)}</dd>
						</dl>
						<ErrorNotice error={create.error} />
					</Card>
				</Stack>
			)}

			<div className="wizard-foot">
				<div className="spread">
					{step > 0 ? (
						<Button
							variant="outlined"
							onClick={() => setStep(step - 1)}
						>
							Back
						</Button>
					) : (
						<Button
							component={Link}
							variant="outlined"
							to={link(
								fromClientId
									? `/audits?client=${fromClientId}`
									: '/audits',
							)}
						>
							Cancel
						</Button>
					)}
					<Box display="flex" gap="var(--s2)">
						{step === STEPS.length - 1 ? (
							<>
								<Button
									variant="outlined"
									disabled={
										create.isPending ||
										!canLeave.every(Boolean)
									}
									onClick={() => submit(false)}
								>
									Create audit
								</Button>
								<Button
									loading={create.isPending}
									variant="contained"
									disabled={
										create.isPending ||
										!canLeave.every(Boolean)
									}
									title={
										canLeave.every(Boolean)
											? undefined
											: blockedReason[
													canLeave.findIndex(
														(ok) => !ok,
													)
												]
									}
									onClick={() => submit(true)}
								>
									Create audit and run analyses
								</Button>
							</>
						) : (
							<Button
								variant="contained"
								disabled={!canLeave[step]}
								title={
									canLeave[step]
										? undefined
										: blockedReason[step]
								}
								onClick={() => setStep(step + 1)}
							>
								Continue
							</Button>
						)}
					</Box>
				</div>
			</div>
		</div>
	);
}
